import { Reveal, PartnerBadge } from "./shared";

const TESTIMONIALS = [
  {
    quote:
      "We were sitting at 200/200 on Account Health when Protego's alert caught an automated carrier scan delay cluster in our Midwest 3PL. Saved our primary ASIN 48 hours before an LSR enforcement strike.",
    name: "Marcus V.",
    role: "Head of Operations, $14M Kitchenware Brand",
  },
  {
    quote:
      "Most consultants try to sell panic. Haroon's workshop laid out the exact mathematical difference between Amazon's public receipt and the real internal scoring queue. Indispensable for any seller who can't afford a surprise suspension.",
    name: "Elena Rostova",
    role: "Director of Marketplaces, Multi-Brand Aggregator",
  },
  {
    quote:
      "The split-screen walkthrough alone is worth $10k. Seeing how an account looks 'safe' while silently triggering brand inauthenticity flags completely changed our prep-center QC process.",
    name: "David Chen",
    role: "Founder, $8M Consumer Electronics Store",
  },
];

const Founder = () => (
  <div className="grid lg:grid-cols-12 gap-10 items-start">
    <Reveal className="lg:col-span-4">
      <div className="relative">
        <img
          data-testid="speaker-photo"
          src="/assets/haroon.jpg"
          alt="Haroon Naseer, Founder of Protego AI"
          className="w-full rounded-2xl border border-slate-200 shadow-lg"
          loading="lazy"
        />
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-3">
          Haroon Naseer — Founder & Lead Forensic Strategist
        </p>
        <a
          data-testid="founder-linkedin-link"
          href="https://www.linkedin.com/in/iamharoon/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 font-mono text-[11px] font-semibold tracking-wide text-white bg-[#0A66C2] hover:bg-[#084d94] rounded-full px-4 py-2 transition-colors duration-200"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
            <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
          </svg>
          Connect on LinkedIn
        </a>
      </div>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-8">
      <p className="font-mono text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700">
        Who's actually building this
      </p>
      <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 mt-4">
        I'm not an attorney. I'm the operator who's spent 10 years rescuing 2,000+ Amazon accounts.
      </h2>
      <div className="text-slate-600 text-[15px] leading-relaxed mt-6 space-y-4 max-w-2xl">
        <p>
          Hi — I'm <b>Haroon Naseer</b>. For the past <b>10 years</b>, I've worked with{" "}
          <b>over 2,000 Amazon seller accounts</b> on Plans of Action, Section 3 reinstatements, and
          account-health emergencies. If Amazon has flagged, warned, deactivated, or suspended it — I've
          probably fixed it before.
        </p>
        <p>
          In those 10 years I watched sellers pay "Amazon attorneys" $5,000–$15,000 for a POA, or
          reinstatement mills $1,500 for a template written for 400 other people — and most of the time
          they still don't get reinstated. It's the biggest legally-permitted rip-off in e-commerce, and
          it made me angry enough to build a solution.
        </p>
        <p>
          So I built <b>Protego AI</b> — the reinstatement engine behind Section3.ai. It reads your
          Seller Central data, cross-references it against thousands of real approvals and rejections I've
          personally worked on, and flags risk in the exact structure Amazon investigators are trained to
          look for. Then <b>I personally review every account</b>.
        </p>
      </div>
      <div className="flex flex-wrap gap-3 mt-7">
        {[
          ["2,000+", "Seller accounts helped"],
          ["10 yrs", "In the trenches"],
          ["$197", "Flat, one-time"],
        ].map(([v, l]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
            <p className="font-mono text-lg font-bold text-slate-900">{v}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{l}</p>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <PartnerBadge />
      </div>
    </Reveal>
  </div>
);

const Team = () => (
  <div className="mt-20">
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700">
        On the ground
      </p>
      <h3 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-900 mt-3">
        Meeting operators at major industry summits
      </h3>
    </Reveal>
    <div className="grid md:grid-cols-2 gap-5 mt-8">
      {[
        ["/assets/team-1.jpg", "The Protego AI team at their booth", "Live telemetry demonstrations at seller summit booth"],
        ["/assets/team-2.jpg", "The Protego AI team at Prosper Show", "Prosper Show, Las Vegas — Booth 509"],
      ].map(([src, alt, cap], i) => (
        <Reveal key={src} delay={i * 0.08}>
          <figure data-testid={`team-photo-${i + 1}`}>
            <img
              src={src}
              alt={alt}
              className="w-full aspect-[4/3] object-cover rounded-2xl border border-slate-200 shadow-sm"
              loading="lazy"
            />
            <figcaption className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-3">
              {cap}
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Social = () => (
  <section data-testid="social-section" className="py-20 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Founder />
      <Team />

      <div className="mt-20">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.2em] font-semibold text-indigo-700">
            Operator feedback
          </p>
          <h3 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-slate-900 mt-3">
            Sellers who stopped trusting green
          </h3>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08} className="h-full">
              <figure
                data-testid={`testimonial-${i + 1}`}
                className="h-full bg-white rounded-2xl border border-slate-200 p-8 flex flex-col transition-all duration-300 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-100/60"
              >
                <span className="font-display text-5xl leading-none text-indigo-200">"</span>
                <blockquote className="text-sm text-slate-600 leading-relaxed flex-1 -mt-3">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-slate-100">
                  <p className="font-display font-bold text-slate-900 text-sm">{t.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                    {t.role}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
